import { useEffect, useState } from 'react';
import axios from 'axios';
import { getAllCompany, getAllQuestions } from '../../../../util/DatabaseUtil';
import {
  BusinessOutlined,
  QuestionAnswerOutlined,
  FilterListOutlined,
  SearchOutlined,
  AddCircleOutline,
  Edit,
  DeleteOutline,
  Save,
  Cancel,
  DescriptionOutlined,
  TrendingUp,
} from '@mui/icons-material';

const Companyquestion = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [selectedCompanyName, setSelectedCompanyName] = useState('');
  const [questions, setQuestions] = useState([]);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [newQuestion, setNewQuestion] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({
    totalCompanies: 0,
    totalQuestions: 0,
  });
  const server_url = process.env.REACT_APP_API_URI;

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await getAllCompany();
        const companiesData = response.businesses || [];
        setCompanies(companiesData);
        setStats((prev) => ({ ...prev, totalCompanies: companiesData.length }));
      } catch (error) {
        console.error('Error fetching companies:', error);
      }
    };
    fetchCompanies();
  }, []);

  useEffect(() => {
    const fetchQuestions = async () => {
      if (!selectedCompanyId) return;
      try {
        setLoading(true);
        const response = await getAllQuestions(selectedCompanyId);
        const questionsData = response.questions || [];
        setQuestions(questionsData);
        setFilteredQuestions(questionsData);
        setStats((prev) => ({ ...prev, totalQuestions: questionsData.length }));
      } catch (error) {
        console.error('Error fetching questions:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchQuestions();
  }, [selectedCompanyId]);

  useEffect(() => {
    let filtered = [...questions];

    if (searchTerm) {
      filtered = filtered.filter(
        (q) =>
          q?.question?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          q?.description?.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    setFilteredQuestions(filtered);
  }, [searchTerm, questions]);

  const handleEdit = (id, question, description) => {
    setEditingId(id);
    setEditText(question);
    setEditDescription(description);
  };

  const handleSave = async (id) => {
    try {
      await axios.put(`${server_url}/businessQuestion/update-question`, {
        businessId: selectedCompanyId,
        questionId: id,
        question: editText,
        description: editDescription,
      });
      setQuestions((prevData) =>
        prevData.map((q) =>
          q._id === id
            ? { ...q, question: editText, description: editDescription }
            : q,
        ),
      );
      setEditingId(null);
    } catch (error) {
      console.error('Error updating question:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this question?'))
      return;
    try {
      await axios.delete(`${server_url}/businessQuestion/delete-question`, {
        data: { businessId: selectedCompanyId, questionId: id },
      });
      setQuestions((prevData) => prevData.filter((q) => q._id !== id));
    } catch (error) {
      console.error('Error deleting question:', error);
    }
  };

  const handleAddQuestion = async () => {
    if (!newQuestion.trim()) return;
    try {
      const response = await axios.post(
        `${server_url}/businessQuestion/add-question`,
        {
          businessId: selectedCompanyId,
          question: newQuestion,
          description: newDescription,
        },
      );
      setQuestions([...questions, response.data.question]);
      setNewQuestion('');
      setNewDescription('');
    } catch (error) {
      console.error('Error adding question:', error);
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
          Company Questions
        </h1>
        <p className="text-gray-600">
          Manage questions for business partners and companies
        </p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-indigo-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">
                Total Companies
              </p>
              <h3 className="text-2xl font-bold text-gray-800">
                {stats.totalCompanies}
              </h3>
            </div>
            <div className="bg-indigo-100 p-3 rounded-full">
              <BusinessOutlined className="text-indigo-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Questions</p>
              <h3 className="text-2xl font-bold text-gray-800">
                {stats.totalQuestions}
              </h3>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <QuestionAnswerOutlined className="text-blue-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Company Selector */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <FilterListOutlined className="text-gray-500 mr-1" fontSize="small" />
          Select Company
        </label>
        <select
          className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          value={selectedCompanyId}
          onChange={(e) => {
            const selectedId = e.target.value;
            setSelectedCompanyId(selectedId);
            setSearchTerm('');
            setEditingId(null);

            const selectedCompany = companies.find((c) => c._id === selectedId);
            if (selectedCompany) {
              setSelectedCompanyName(selectedCompany.name);
            }
          }}
        >
          <option value="">Choose a company to manage questions</option>
          {companies.map((company) => (
            <option key={company._id} value={company._id}>
              {company?.name || 'Unnamed Company'}
            </option>
          ))}
        </select>
      </div>

      {selectedCompanyId && (
        <>
          {/* Add Question Form */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <AddCircleOutline className="text-blue-600" />
              Add New Question
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Question
                </label>
                <input
                  type="text"
                  placeholder="Enter question text..."
                  className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={newQuestion}
                  onChange={(e) => setNewQuestion(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  placeholder="Enter question description..."
                  rows="3"
                  className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                />
              </div>
              <button
                onClick={handleAddQuestion}
                disabled={!newQuestion.trim()}
                className="flex items-center gap-2 bg-gradient-to-r from-green-600 to-green-700 text-white px-6 py-3 rounded-lg hover:from-green-700 hover:to-green-800 transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <AddCircleOutline />
                Add Question
              </button>
            </div>
          </div>

          {/* Search Bar */}
          {questions.length > 0 && (
            <div className="bg-white rounded-lg shadow-md p-4 mb-4">
              <div className="relative">
                <SearchOutlined className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search questions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          )}

          {/* Questions List */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Questions for {selectedCompanyName || 'Selected Company'}
            </h3>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : filteredQuestions.length > 0 ? (
              <div className="space-y-4">
                {filteredQuestions.map((question) => (
                  <div
                    key={question._id}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    {editingId === question._id ? (
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Question
                          </label>
                          <input
                            type="text"
                            className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Description
                          </label>
                          <textarea
                            rows="3"
                            className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            value={editDescription}
                            onChange={(e) => setEditDescription(e.target.value)}
                          />
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleSave(question._id)}
                            className="flex items-center gap-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                          >
                            <Save fontSize="small" />
                            Save
                          </button>
                          <button
                            onClick={() => setEditingId(null)}
                            className="flex items-center gap-1 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                          >
                            <Cancel fontSize="small" />
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-start gap-2 mb-2">
                            <QuestionAnswerOutlined
                              className="text-blue-500 mt-1"
                              fontSize="small"
                            />
                            <div>
                              <p className="text-lg font-semibold text-gray-800">
                                {question?.question || 'No Question Text'}
                              </p>
                              <div className="flex items-start gap-2 mt-1">
                                <DescriptionOutlined
                                  className="text-gray-400 mt-0.5"
                                  fontSize="small"
                                />
                                <p className="text-gray-600 text-sm">
                                  {question?.description ||
                                    'No Description Provided'}
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2 ml-4">
                          <button
                            onClick={() =>
                              handleEdit(
                                question._id,
                                question.question,
                                question.description,
                              )
                            }
                            className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                            title="Edit Question"
                          >
                            <Edit fontSize="small" />
                          </button>
                          <button
                            onClick={() => handleDelete(question._id)}
                            className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                            title="Delete Question"
                          >
                            <DeleteOutline fontSize="small" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-12 text-center">
                <QuestionAnswerOutlined className="text-gray-400 text-5xl mb-2" />
                <p className="text-gray-500 text-lg font-medium">
                  {searchTerm
                    ? 'No questions match your search'
                    : 'No questions available'}
                </p>
                <p className="text-gray-400 text-sm">
                  {searchTerm
                    ? 'Try adjusting your search term'
                    : 'Add your first question using the form above'}
                </p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Empty State - No Company Selected */}
      {!selectedCompanyId && (
        <div className="bg-white rounded-lg shadow-md p-12 text-center">
          <BusinessOutlined className="text-gray-400 text-6xl mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">
            Select a Company
          </h3>
          <p className="text-gray-500">
            Choose a company from the dropdown above to manage their questions
          </p>
        </div>
      )}
    </div>
  );
};

export default Companyquestion;
